import { Modal } from '@fullstory/components';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SpaceAndSectionSelect, SpaceAndSectionSelectProps } from '../SpaceAndSectionSelect';

interface SpaceObjectModalProps
  extends Omit<
    SpaceAndSectionSelectProps,
    'touchedSpaceSelect' | 'setTouchedSpaceSelect' | 'touchedSectionSelect' | 'variant'
  > {
  onCloseSignal: () => void;
  open: boolean;
  onSave: () => void;
}

export const SpaceObjectModal: React.FC<SpaceObjectModalProps> = ({
  onCloseSignal,
  onSave,
  open,
  ...rest
}) => {
  const intl = useIntl();
  const [touchedSpaceSelect, setTouchedSpaceSelect] = useState(false);
  const [touchedSectionSelect, setTouchedSectionSelect] = useState(false);

  const { mode, selectedSpace, selectedSection, sectionRequired } = rest;

  const onClosed = useCallback(() => {
    setTouchedSpaceSelect(false);
    setTouchedSectionSelect(false);
  }, []);

  return (
    <Modal
      data-component="SpaceObjectModal"
      onCloseSignal={onCloseSignal}
      open={open}
      onClosed={onClosed}
      size="small"
    >
      <Modal.Header onCloseSignal={onCloseSignal}>
        {mode === 'add' && (
          <FormattedMessage
            defaultMessage="Add to a space"
            id="ezVRBg"
            description="Modal header indicating that object will be added to a space as a link and possibly featured"
          />
        )}
        {mode === 'feature' && (
          <FormattedMessage
            defaultMessage="Add to Featured"
            id="YTrk1S"
            description="Modal header indicating that object will be added to a space's Featured section"
          />
        )}
        {mode === 'transfer' && (
          <FormattedMessage
            defaultMessage="Transfer to a different space"
            id="pf1thO"
            description="Modal header indicating that an object will be transferred to another space"
          />
        )}
        {mode === 'save' && (
          <FormattedMessage
            defaultMessage="Which space would you like to save to? "
            id="jB2Qz2"
            description="Space that the object will be saved to"
          />
        )}
      </Modal.Header>
      <Modal.Body>
        <SpaceAndSectionSelect
          touchedSpaceSelect={touchedSpaceSelect}
          setTouchedSpaceSelect={setTouchedSpaceSelect}
          touchedSectionSelect={touchedSectionSelect}
          variant="outline"
          {...rest}
        />
      </Modal.Body>
      <Modal.FooterActions
        primaryAction={{
          action: () => {
            if (!selectedSpace) {
              setTouchedSpaceSelect(true);
            } else if (
              (mode === 'add' || mode === 'feature') &&
              !selectedSection &&
              sectionRequired
            ) {
              setTouchedSectionSelect(true);
            } else {
              onSave();
              setTouchedSpaceSelect(false);
              setTouchedSectionSelect(false);
            }
          },
          text: intl.formatMessage(
            {
              defaultMessage:
                '{mode, select, transfer {Transfer} add {Add} save {Save} other {Add}}',
              id: 'zM/Zb2',
            },
            { mode },
          ),
        }}
        secondaryAction={{
          action: onCloseSignal,
          text: intl.formatMessage({
            defaultMessage: 'Cancel',
            id: 'zl74I7',
            description: 'Cancel and close the modal popup',
          }),
        }}
      />
    </Modal>
  );
};
SpaceObjectModal.displayName = 'SpaceObjectModal';
